import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Product, Customer, CartItem } from '../types';
import { productService } from '../services/productService';
import { customerService } from '../services/customerService';
import { salesService, CreateSaleRequest } from '../services/salesService';
import { ThermalPrinter, ThermalReceiptData } from '../utils/thermalPrinter';
import ConfirmationModal from '../components/ConfirmationModal';
import ProductPreviewModal from '../components/ProductPreviewModal';
import { 
  ShoppingCart, 
  Search, 
  Plus, 
  Minus, 
  X, 
  CreditCard,
  Users,
  Trash2,
  User,
  Printer,
  Edit3,
  Eye
} from 'lucide-react';
import { customToast } from '../utils/customToast';
import { useTitle } from '../hooks/useTitle';
import { useCurrency } from '../hooks/useCurrency';

const POSPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, isLoading: isAuthLoading, validateToken } = useAuth();

  // Add CSS to handle webkit spinners
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      input[type=number]::-webkit-outer-spin-button,
      input[type=number]::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const { getSetting } = useSettings();
  const { formatCurrency, currencySymbol } = useCurrency();
  
  // Set page title
  useTitle('Point of Sale');

  // State management
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Loading states
  const [productsLoading, setProductsLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(false);
  
  // Search and filters
  const [productSearch, setProductSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  
  // Walk-in customer support
  const [walkInCustomer, setWalkInCustomer] = useState({
    name: '',
    phone: ''
  });
  const [isWalkInMode, setIsWalkInMode] = useState(true); // Default to walk-in mode
  
  // Debug log
  useEffect(() => {
    console.log('walkInCustomer state:', walkInCustomer);
  }, [walkInCustomer]);
  
  // Payment details
  const [discount, setDiscount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'bank_transfer' | 'other'>('cash');
  const [includeTax, setIncludeTax] = useState(false);
  const [taxRate, setTaxRate] = useState(5);
  const [isProcessing, setIsProcessing] = useState(false);

  // UI states
  const [activeTab, setActiveTab] = useState<'products' | 'customers' | 'quick-entry'>('products');
  
  // Track if initial load has been done to prevent double-loading
  const initialLoadDoneRef = useRef(false);
  
  // Quick Entry states
  const [quickEntryData, setQuickEntryData] = useState({
    name: '',
    quantity: '',
    sellPrice: '',
    purchasePrice: '',
    customUnit: '', // For custom unit entry (e.g., Kilo, Dozen, Meter)
    saveToDatabase: false
  });
  
  // Print confirmation modal
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [pendingReceiptData, setPendingReceiptData] = useState<ThermalReceiptData | null>(null);
  const [printModalLoading, setPrintModalLoading] = useState(false);
  
  // Product preview modal
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [previewProductLoading, setPreviewProductLoading] = useState(false);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await productService.getProducts(
        { search: productSearch, status: 'active', sortBy: 'created_at', sortOrder: 'desc' },
        1,
        50,
        true // Include custom fields
      );
      setProducts(response.data);
    } catch (error: any) {
      console.error('Failed to load products:', error);
      
      // Only clear products if we don't have any data already
      if (products.length === 0) {
        setProducts([]);
      }
      
      // Provide more specific error messages but only for non-auth errors
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          // Let auth system handle redirects, don't show toast
          return;
        } else if (status === 403) {
          customToast.error('You do not have permission to access products.');
        } else if (status === 404) {
          customToast.error('Products endpoint not found. Please contact support.');
        } else if (error.response.data?.message) {
          customToast.error(error.response.data.message);
        } else {
          customToast.error('Failed to load products. Please try again.');
        }
      } else if (error.request) {
        // Only show connection error if we don't have existing data
        if (products.length === 0) {
          customToast.error('Cannot reach server. Please check your connection.');
        }
      } else {
        customToast.error('Failed to load products. Please try again.');
      }
    } finally {
      setProductsLoading(false);
    }
  };

  const loadCustomers = async (search: string = '') => {
    try {
      setCustomersLoading(true);
      const response = await customerService.getCustomers(
        search ? { search } : {},
        1,
        50 // Load more customers initially
      );
      setCustomers(response.data);
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      
      // Only clear customers if we don't have any data already
      if (customers.length === 0) {
        setCustomers([]);
      }
      
      // Provide more specific error messages but only for non-auth errors
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          // Let auth system handle redirects, don't show toast
          return;
        } else if (status === 403) {
          customToast.error('You do not have permission to access customers.');
        } else if (status === 404) {
          customToast.error('Customers endpoint not found. Please contact support.');
        } else if (error.response.data?.message) {
          customToast.error(error.response.data.message);
        } else {
          customToast.error('Failed to load customers. Please try again.');
        }
      } else if (error.request) {
        // Only show connection error if we don't have existing data
        if (customers.length === 0) {
          customToast.error('Cannot reach server. Please check your connection.');
        }
      } else {
        customToast.error('Failed to load customers. Please try again.');
      }
    } finally {
      setCustomersLoading(false);
    }
  };

  // Handle Quick Entry Add to Cart
  const handleQuickEntryAddToCart = async () => {
    const { name, quantity, sellPrice, purchasePrice, customUnit, saveToDatabase } = quickEntryData;
    
    if (!name || !quantity || !sellPrice) {
      customToast.error('Please fill in all required fields');
      return;
    }

    const qty = parseFloat(quantity);
    const price = parseFloat(sellPrice);
    const cost = purchasePrice ? parseFloat(purchasePrice) : 0;
    const finalUnit = customUnit || 'piece'; // Use custom unit if provided, else default

    if (qty <= 0 || price <= 0) {
      customToast.error('Quantity and price must be greater than 0');
      return;
    }

    // Create a temporary product object for the cart
    const tempProduct: Product = {
      id: -Date.now(), // Use negative timestamp as temporary ID
      name,
      selling_price: price,
      purchase_price: cost || 0,
      stock_quantity: 999999, // Large number for manual entries
      minimum_stock_level: 0,
      brand: 'Manual Entry',
      category: 'Quick Entry',
      unit: finalUnit,
      status: 'active' as const,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      is_manual_entry: true // Flag to identify manual entries
    };

    // Add to cart
    const existingItem = cart.find(item => item.product.name === name && item.product.is_manual_entry);
    
    if (existingItem) {
      // Update existing manual entry
      setCart(prevCart => 
        prevCart.map(item => 
          item.product.name === name && item.product.is_manual_entry
            ? { 
                ...item, 
                quantity: item.quantity + qty,
                total: (item.quantity + qty) * price
              }
            : item
        )
      );
    } else {
      // Add new manual entry
      const newCartItem: CartItem = {
        product: tempProduct,
        quantity: qty,
        total: qty * price
      };
      setCart(prevCart => [...prevCart, newCartItem]);
    }

    // Optionally save to database
    if (saveToDatabase) {
      try {
        await productService.createProduct({
          name,
          selling_price: price,
          purchase_price: cost || 0,
          stock_quantity: 0, // Start with 0 stock for quick entries
          minimum_stock_level: 0,
          brand: 'Quick Entry',
          category: 'Quick Entry',
          unit: finalUnit,
          description: 'Added via Quick Entry'
        });
        customToast.success(`Product "${name}" saved to database and added to cart`);
      } catch (error) {
        customToast.warning(`Added to cart but failed to save to database. You can still complete the sale.`);
      }
    } else {
      customToast.success(`"${name}" added to cart`);
    }

    // Clear the form
    setQuickEntryData({
      name: '',
      quantity: '',
      sellPrice: '',
      purchasePrice: '',
      customUnit: '',
      saveToDatabase: false
    });
  };

  // Search effects with better debouncing
  useEffect(() => {
    // Don't search if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    const debounceTimer = setTimeout(() => {
      loadProducts();
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [productSearch, isAuthLoading, user]);

  useEffect(() => {
    // Don't search if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    const debounceTimer = setTimeout(() => {
      if (customerSearch.trim()) {
        // Search with term
        loadCustomers(customerSearch);
      } else {
        // Load all customers when search is empty
        loadCustomers();
      }
    }, 500);
    return () => clearTimeout(debounceTimer);
  }, [customerSearch, isAuthLoading, user]);

  // Load initial data only once and after auth is ready
  useEffect(() => {
    // Don't load data if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    // Only load once
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    
    // Load products and customers initially
    loadProducts();
    loadCustomers();
  }, [isAuthLoading, user]);

  // Handle tab switching - only reload products when switching TO products tab from another tab
  useEffect(() => {
    if (isAuthLoading || !user) return;
    
    // Don't reload on initial mount, only on tab switches after initial load
    if (!initialLoadDoneRef.current) return;
    
    if (activeTab === 'products') {
      loadProducts();
    }
  }, [activeTab, isAuthLoading, user]);

  // Clear search inputs when switching tabs
  useEffect(() => {
    setProductSearch('');
    setCustomerSearch('');
  }, [activeTab]);

  // Cart operations
  const addToCart = (product: Product) => {
    if (product.stock_quantity <= 0) {
      customToast.error(`${product.name} is out of stock`);
      return;
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      
      if (existingItem) {
        if (existingItem.quantity >= product.stock_quantity) {
          customToast.error(`Not enough stock for ${product.name}`);
          return prevCart;
        }
        return prevCart.map(item =>
          item.product.id === product.id
            ? { 
                ...item, 
                quantity: item.quantity + 1, 
                total: (item.quantity + 1) * (Number(product.selling_price) || 0)
              }
            : item
        );
      }
      
      return [...prevCart, {
        product,
        quantity: 1,
        total: Number(product.selling_price) || 0
      }];
    });
  };

  const updateCartQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
    } else {
      setCart(prevCart => prevCart.map(item =>
        item.product.id === productId
          ? { 
              ...item, 
              quantity: newQuantity,
              total: newQuantity * (Number(item.product.selling_price) || 0)
            }
          : item
      ));
    }
  }

  const handleQuantityInputChange = (productId: number, value: string) => {
    const newQuantity = parseInt(value, 10)
    if (!isNaN(newQuantity) && newQuantity > 0) {
      updateCartQuantity(productId, newQuantity)
    } else if (value === '' || newQuantity === 0) {
      // If empty or 0, remove from cart
      updateCartQuantity(productId, 0)
    }
  }

  const handlePreviewProduct = async (product: Product) => {
    // Open modal immediately with the list product data
    setPreviewProduct(product);
    setPreviewProductLoading(true);

    try {
      // Fetch the full product with custom fields
      const fullProduct = await productService.getProduct(product.id);
      setPreviewProduct(fullProduct);
    } catch (error) {
      customToast.error('Failed to load product details');
      console.error('Failed to load product:', error);
    } finally {
      setPreviewProductLoading(false);
    }
  };

  const removeFromCart = (productId: number) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setSelectedCustomer(null);
    setWalkInCustomer({ name: '', phone: '' });
    setDiscount(0);
    setPaidAmount(0);
  };

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
    const discountAmount = (subtotal * discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = includeTax ? (taxableAmount * taxRate) / 100 : 0;
    const total = taxableAmount + taxAmount;
    
    return {
      subtotal,
      discountAmount,
      taxAmount,
      total
    };
  };

  const { subtotal, discountAmount, taxAmount, total } = calculateTotals();
  
  // Safe versions for display
  const safeSubtotal = subtotal || 0;
  const safeDiscountAmount = discountAmount || 0;
  const safeTaxAmount = taxAmount || 0;
  const safeTotal = total || 0;

  // Print preview for current cart
  const printPreview = () => {
    if (cart.length === 0) {
      customToast.error('Cart is empty');
      return;
    }

    const receiptData: ThermalReceiptData = {
      saleId: 0, // Preview mode
      customerName: selectedCustomer?.name || walkInCustomer.name,
      customerPhone: selectedCustomer?.phone || walkInCustomer.phone,
      items: cart,
      subtotal: subtotal,
      discount: discount,
      discountAmount: discountAmount,
      taxRate: includeTax ? taxRate : 0,
      taxAmount: taxAmount,
      total: total,
      paidAmount: paidAmount || total,
      changeAmount: Math.max(0, (paidAmount || total) - total),
      dueAmount: Math.max(0, total - (paidAmount || total)), // Add due amount for preview
      paymentMethod: paymentMethod,
      saleDate: new Date().toISOString(),
      currencySymbol: currencySymbol,
      storeName: user?.shop?.shop_name || getSetting('print_store_name', 'NextGen - Shop'),
      storePhone: getSetting('print_phone', '+92 348 119 6365'),
      storeAddress: getSetting('print_address', '123 Main Street, Suite 456, Springfield, USA 12345')
    };
    
    ThermalPrinter.printHtmlReceipt(receiptData);
  };

  // Handle print confirmation
  const handlePrintConfirm = async () => {
    setPrintModalLoading(true);
    
    if (pendingReceiptData) {
      try {
        await ThermalPrinter.printToThermalPrinter(pendingReceiptData);
        customToast.success('Receipt printed successfully!');
      } catch (error) {
        console.error('Failed to print receipt:', error);
        customToast.error('Failed to print receipt');
      }
    }
    
    // Clean up and clear cart regardless of print success/failure
    setPrintModalLoading(false);
    setShowPrintModal(false);
    setPendingReceiptData(null);
    clearCart();
    loadProducts();
  };

  const handlePrintCancel = () => {
    // Just clean up without printing
    setShowPrintModal(false);
    setPendingReceiptData(null);
    clearCart();
    loadProducts();
  };

  // Process sale
  const processSale = async () => {
    if (cart.length === 0) {
      customToast.error('Cart is empty');
      return;
    }

    // Check if user is authenticated
    if (!user) {
      customToast.error('You must be logged in to process sales');
      return;
    }

    // Walk-in customer name is optional now for full payments
    // But required for partial payments
    const isPartialPayment = paidAmount < total;
    if (isWalkInMode && isPartialPayment && !walkInCustomer.name.trim()) {
      customToast.error('Customer name is required for partial payments. Please add customer details or collect full payment.');
      return;
    }

    setIsProcessing(true);

    try {
      // Validate authentication before processing
      const isTokenValid = await validateToken();
      if (!isTokenValid) {
        customToast.error('Your session has expired. Please log in again.');
        return;
      }

      // Determine payment status
      let paymentStatus: 'paid' | 'partial' | 'due' = 'paid';
      if (paidAmount < total) {
        paymentStatus = paidAmount > 0 ? 'partial' : 'due';
      }

      const saleData: CreateSaleRequest = {
        customer_id: selectedCustomer?.id || null,
        customer_name: isWalkInMode 
          ? (walkInCustomer.name.trim() || 'Walk-in Customer') 
          : selectedCustomer?.name || 'Walk-in Customer',
        customer_phone: isWalkInMode ? walkInCustomer.phone : undefined,
        add_customer: isWalkInMode && !!walkInCustomer.name.trim() && paymentStatus !== 'paid', // Add customer for partial payments
        items: cart.map(item => ({
          product_id: item.product.is_manual_entry ? undefined : item.product.id,
          product_name: item.product.is_manual_entry ? item.product.name : undefined,
          quantity: item.quantity,
          unit_price: Number(item.product.selling_price),
          unit: item.product.unit
        })),
        subtotal: subtotal,
        tax_amount: taxAmount,
        discount_amount: discountAmount,
        total_amount: total,
        paid_amount: paidAmount,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        notes: ''
      };

      console.log('Sale data being sent:', saleData);
      console.log('Payment method being sent:', saleData.payment_method);
      console.log('Payment method type:', typeof saleData.payment_method);
      console.log('Customer ID being sent:', saleData.customer_id);
      console.log('Customer name being sent:', saleData.customer_name);
      console.log('isWalkInMode:', isWalkInMode);
      console.log('walkInCustomer:', walkInCustomer);
      console.log('selectedCustomer:', selectedCustomer);

      const sale = await salesService.createSale(saleData);
      
      customToast.success('Sale completed successfully!');
      
      // Prepare receipt data and show print confirmation modal
      const receiptData: ThermalReceiptData = {
        saleId: sale.shop_display_id || sale.shop_sequence_id || sale.id, // Use shop-scoped ID
        customerName: selectedCustomer?.name || (walkInCustomer.name.trim() || undefined),
        customerPhone: selectedCustomer?.phone || walkInCustomer.phone,
        items: cart,
        subtotal: subtotal,
        discount: discount,
        discountAmount: discountAmount,
        taxRate: includeTax ? taxRate : 0,
        taxAmount: taxAmount,
        total: total,
        paidAmount: paidAmount,
        changeAmount: Math.max(0, paidAmount - total),
        dueAmount: Math.max(0, total - paidAmount), // Add due amount
        paymentMethod: paymentMethod,
        saleDate: new Date().toISOString(),
        storeName: user?.shop?.shop_name || getSetting('print_store_name', 'NextGen - Shop'),
        storePhone: getSetting('print_phone', '+92 348 119 6365'),
        storeAddress: getSetting('print_address', '123 Main Street, Suite 456, Springfield, USA 12345'),
        currencySymbol: currencySymbol
      };
      
      setPendingReceiptData(receiptData);
      setShowPrintModal(true);

    } catch (error: any) {
      console.error('Sale creation error:', error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          customToast.error('Authentication required. Please log in again.');
        } else if (status === 403) {
          customToast.error('You do not have permission to process sales.');
        } else if (status === 422 && data?.errors) {
          // Validation errors
          const validationErrors = Object.values(data.errors).flat().join(', ');
          customToast.error(`Validation error: ${validationErrors}`);
        } else if (data?.message) {
          customToast.error(data.message);
        } else {
          customToast.error('Failed to process sale. Please try again.');
        }
      } else if (error.request) {
        customToast.error('Cannot reach server. Please check your connection.');
      } else {
        customToast.error(error.message || 'Failed to process sale. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className={`${
        theme === 'dark' ? 'bg-slate-800' : 'bg-white'
      } rounded-xl sm:rounded-2xl p-4 sm:p-6 border ${
        theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
      }`}>
        <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          Point of Sale
        </h1>
        <p className={`mt-1 sm:mt-2 text-sm sm:text-base ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          Select products and process sales transactions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Left Panel - Product/Customer Selection */}
        <div className={`${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        } rounded-xl sm:rounded-2xl border ${
          theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
        }`}>
          <div className="p-4 sm:p-6">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-1 mb-4 sm:mb-6">
              <button
                onClick={() => setActiveTab('products')}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                  activeTab === 'products'
                    ? 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ShoppingCart size={16} />
                <span className="text-sm sm:text-base">Products</span>
              </button>
              <button
                onClick={() => setActiveTab('quick-entry')}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                  activeTab === 'quick-entry'
                    ? 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Edit3 size={16} />
                <span className="text-sm sm:text-base">Quick Entry</span>
              </button>
              <button
                onClick={() => setActiveTab('customers')}
                className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-colors touch-manipulation ${
                  activeTab === 'customers'
                    ? 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Users size={16} />
                <span className="text-sm sm:text-base">Customers</span>
              </button>
            </div>

            {/* Walk-in Customer Input - Above search for customers tab */}
            {activeTab === 'customers' && (
              <div className={`mb-4 sm:mb-6 p-4 rounded-xl border ${
                theme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-green-50 border-green-200'
              }`}>
                <h4 className={`font-medium mb-3 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Walk-in Customer
                </h4>
                <input
                  type="text"
                  placeholder="Enter customer name..."
                  value={walkInCustomer.name}
                  onChange={(e) => {
                    console.log('Customer name input changed:', e.target.value);
                    setWalkInCustomer(prev => ({ ...prev, name: e.target.value }));
                  }}
                  className={`w-full px-3 py-2 text-sm rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-slate-600 border-slate-500 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            )}

            {/* Search */}
            {activeTab !== 'quick-entry' && (
              <div className="relative mb-4 sm:mb-6">
                <Search size={16} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input
                  type="text"
                  placeholder={activeTab === 'products' ? 'Search products...' : 'Search customers...'}
                  value={activeTab === 'products' ? productSearch : customerSearch}
                  onChange={(e) => {
                    if (activeTab === 'products') {
                      setProductSearch(e.target.value);
                    } else {
                      setCustomerSearch(e.target.value);
                    }
                  }}
                  className={`w-full pl-10 pr-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            )}

            {/* Content */}
            <div>
              {activeTab === 'products' ? (
                <div className="max-h-80 sm:max-h-96 overflow-y-auto">
                  <div className="space-y-2 sm:space-y-3">
                  {productsLoading ? (
                    <div className="space-y-2 sm:space-y-3">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div
                          key={index}
                          className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border animate-pulse ${
                            theme === 'dark'
                              ? 'bg-slate-700 border-slate-600'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            {/* Left section: Product info */}
                            <div className="flex-1 min-w-0">
                              {/* Product name skeleton */}
                              <div className={`h-4 w-28 rounded mb-1 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              
                              {/* Brand • Category skeleton */}
                              <div className={`h-3 w-36 rounded mb-2 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              
                              {/* Price and stock row skeleton */}
                              <div className="flex items-center space-x-2 mt-1">
                                <div className={`h-4 w-16 rounded ${
                                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                                }`} />
                                <div className={`h-3 w-20 rounded ${
                                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                                }`} />
                              </div>
                            </div>
                            
                            {/* Right section: Action buttons (View + Add/Quantity controls) */}
                            <div className="ml-3 flex-shrink-0 flex items-center gap-1.5 sm:gap-2">
                              {/* View button */}
                              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              
                              {/* Add button or quantity controls skeleton (3 buttons + input) */}
                              <div className="flex items-center gap-1">
                                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${
                                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                                }`} />
                                <div className={`h-7 sm:h-8 w-8 rounded-lg ${
                                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                                }`} />
                                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg ${
                                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                                }`} />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : products.filter(product => product.stock_quantity > 0).length > 0 ? (
                    products
                      .filter(product => product.stock_quantity > 0) // Filter out out-of-stock products
                      .map(product => {
                    const cartItem = cart.find(item => item.product.id === product.id);
                    const inCart = !!cartItem;
                    
                    return (
                      <div
                        key={product.id}
                        className={`p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all hover:shadow-md touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 hover:border-slate-500'
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-semibold text-sm sm:text-base truncate ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                              {product.name}
                            </h3>
                            <p className={`text-xs sm:text-sm truncate ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {product.brand} • {product.category}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              <p className={`font-bold text-sm sm:text-base ${
                                theme === 'dark' ? 'text-green-400' : 'text-green-600'
                              }`}>
                                {formatCurrency(product.selling_price)}
                              </p>
                              <p className={`text-xs sm:text-sm ${
                                product.stock_quantity <= 10
                                  ? 'text-red-500'
                                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                Stock: {product.stock_quantity}
                              </p>
                            </div>
                          </div>
                          <div className="ml-3 flex-shrink-0">
                            {inCart ? (
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <button
                                  onClick={() => handlePreviewProduct(product)}
                                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg border flex items-center justify-center touch-manipulation ${
                                    theme === 'dark'
                                      ? 'border-slate-600 text-gray-400 hover:text-white hover:bg-slate-600'
                                      : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                  }`}
                                  title="Quick view"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => updateCartQuantity(product.id, cartItem.quantity - 1)}
                                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg border flex items-center justify-center touch-manipulation ${
                                    theme === 'dark'
                                      ? 'border-slate-600 text-gray-400 hover:text-white hover:bg-slate-600'
                                      : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                  }`}
                                >
                                  <Minus size={14} />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  max={product.stock_quantity}
                                  value={cartItem.quantity}
                                  onChange={(e) => handleQuantityInputChange(product.id, e.target.value)}
                                  className={`h-7 sm:h-8 text-xs sm:text-sm font-medium border rounded-lg touch-manipulation ${
                                    theme === 'dark'
                                      ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                                  } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                  style={{ 
                                    width: '40px',
                                    textAlign: 'center',
                                    padding: '0',
                                    margin: '0',
                                    boxSizing: 'border-box',
                                    MozAppearance: 'textfield',
                                    WebkitAppearance: 'none'
                                  } as React.CSSProperties & {
                                    MozAppearance?: string;
                                    WebkitAppearance?: string;
                                  }}
                                />
                                <button
                                  onClick={() => updateCartQuantity(product.id, cartItem.quantity + 1)}
                                  disabled={cartItem.quantity >= product.stock_quantity}
                                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg border flex items-center justify-center touch-manipulation ${
                                    cartItem.quantity >= product.stock_quantity
                                      ? 'opacity-50 cursor-not-allowed border-gray-300'
                                      : theme === 'dark'
                                        ? 'border-slate-600 text-gray-400 hover:text-white hover:bg-slate-600'
                                        : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                  }`}
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <button
                                  onClick={() => handlePreviewProduct(product)}
                                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg border flex items-center justify-center touch-manipulation ${
                                    theme === 'dark'
                                      ? 'border-slate-600 text-gray-400 hover:text-white hover:bg-slate-600'
                                      : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                  }`}
                                  title="Quick view"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  onClick={() => addToCart(product)}
                                  className="px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700 touch-manipulation"
                                >
                                  <span className="hidden sm:inline">Add to Cart</span>
                                  <span className="sm:hidden">Add</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                  ) : (
                    <div className="flex flex-col justify-center items-center py-6 sm:py-8">
                      <ShoppingCart size={28} className={`sm:w-8 sm:h-8 mb-2 opacity-50 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <span className={`text-sm mb-2 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        No products found
                      </span>
                      <button
                        onClick={() => loadProducts()}
                        className="text-blue-500 hover:text-blue-600 text-xs sm:text-sm touch-manipulation"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  </div>
                </div>
              ) : activeTab === 'quick-entry' ? (
                <div className="space-y-3 sm:space-y-4">
                  <div className="space-y-3 sm:space-y-4">
                    {/* Product Name */}
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Product Name *
                      </label>
                      <input
                        type="text"
                        value={quickEntryData.name}
                        onChange={(e) => setQuickEntryData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter product name"
                        className={`w-full px-2 sm:px-3 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border transition-colors touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                      />
                    </div>

                    {/* Quantity and Prices Row */}
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Quantity *
                        </label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={quickEntryData.quantity}
                          onChange={(e) => setQuickEntryData(prev => ({ ...prev, quantity: e.target.value }))}
                          placeholder="0"
                          className={`w-full px-2 sm:px-3 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border transition-colors touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Unit (Optional)
                        </label>
                        <input
                          type="text"
                          value={quickEntryData.customUnit}
                          onChange={(e) => setQuickEntryData(prev => ({ ...prev, customUnit: e.target.value }))}
                          placeholder="e.g., Kilo, Dozen"
                          className={`w-full px-2 sm:px-3 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border transition-colors touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Sell Price {currencySymbol} *
                        </label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={quickEntryData.sellPrice}
                          onChange={(e) => setQuickEntryData(prev => ({ ...prev, sellPrice: e.target.value }))}
                          placeholder="0.00"
                          className={`w-full px-2 sm:px-3 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border transition-colors touch-manipulation ${
                            theme === 'dark'
                              ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Purchase Price (Optional) */}
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Purchase Price {currencySymbol} (Optional)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={quickEntryData.purchasePrice}
                        onChange={(e) => setQuickEntryData(prev => ({ ...prev, purchasePrice: e.target.value }))}
                        placeholder="0.00"
                        className={`w-full px-2 sm:px-3 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg border transition-colors touch-manipulation ${
                          theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                      />
                    </div>

                    {/* Save to Database Option */}
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      <input
                        type="checkbox"
                        id="saveToDatabase"
                        checked={quickEntryData.saveToDatabase}
                        onChange={(e) => setQuickEntryData(prev => ({ ...prev, saveToDatabase: e.target.checked }))}
                        className="w-4 h-4 mt-0.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 touch-manipulation"
                      />
                      <label htmlFor="saveToDatabase" className={`text-xs sm:text-sm leading-tight ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Save this product to database for future use
                      </label>
                    </div>

                    {/* Add to Cart Button */}
                    <button
                      onClick={handleQuickEntryAddToCart}
                      disabled={!quickEntryData.name || !quickEntryData.quantity || !quickEntryData.sellPrice}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2.5 sm:py-3 px-3 sm:px-4 text-sm sm:text-base rounded-lg transition-colors flex items-center justify-center space-x-2 touch-manipulation font-medium"
                    >
                      <Plus size={14} className="sm:w-4 sm:h-4" />
                      <span>Add to Cart</span>
                    </button>

                    {/* Calculate Total Display */}
                    {quickEntryData.quantity && quickEntryData.sellPrice && (
                      <div className={`mt-4 p-4 rounded-xl border ${
                        theme === 'dark' ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex justify-between items-center">
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Total:
                          </span>
                          <span className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {formatCurrency(parseFloat(quickEntryData.quantity || '0') * parseFloat(quickEntryData.sellPrice || '0'))}
                          </span>
                        </div>
                        {quickEntryData.purchasePrice && (
                          <div className="flex justify-between items-center mt-2">
                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              Profit:
                            </span>
                            <span className={`text-sm font-medium text-green-600`}>
                              {formatCurrency((parseFloat(quickEntryData.sellPrice || '0') - parseFloat(quickEntryData.purchasePrice || '0')) * parseFloat(quickEntryData.quantity || '0'))}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  {/* Customer List - Scrollable */}
                  <div className="max-h-64 sm:max-h-80 overflow-y-auto space-y-2">
                    {customersLoading ? (
                    <div className="space-y-2 sm:space-y-3">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg sm:rounded-xl border animate-pulse ${
                            theme === 'dark'
                              ? 'bg-slate-700 border-slate-600'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              {/* Name skeleton */}
                              <div className={`h-5 w-28 rounded flex-shrink-0 ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                              
                              {/* Phone skeleton */}
                              <div className={`h-4 w-24 rounded ${
                                theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                              }`} />
                            </div>
                            
                            {/* Selection indicator skeleton */}
                            <div className={`w-5 h-5 rounded-full flex-shrink-0 ml-2 ${
                              theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                            }`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : customers.length > 0 ? (
                    customers.map(customer => (
                    <div
                      key={customer.id}
                      className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                        selectedCustomer?.id === customer.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : theme === 'dark'
                            ? 'bg-slate-700 border-slate-600 hover:border-slate-500'
                            : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        // Allow unselecting by clicking the same customer
                        if (selectedCustomer?.id === customer.id) {
                          setSelectedCustomer(null);
                          setIsWalkInMode(true);
                        } else {
                          setSelectedCustomer(customer);
                          setIsWalkInMode(false);
                          setWalkInCustomer({ name: '', phone: '' });
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className={`font-semibold ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                              {customer.name}
                            </h3>
                            {customer.customer_type && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                customer.customer_type === 'vip'
                                  ? theme === 'dark'
                                    ? 'bg-purple-900/50 text-purple-300 border border-purple-700'
                                    : 'bg-purple-100 text-purple-700'
                                  : customer.customer_type === 'business'
                                    ? theme === 'dark'
                                      ? 'bg-blue-900/50 text-blue-300 border border-blue-700'
                                      : 'bg-blue-100 text-blue-700'
                                    : customer.customer_type === 'wholesale'
                                      ? theme === 'dark'
                                        ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-700'
                                        : 'bg-indigo-100 text-indigo-700'
                                      : customer.customer_type === 'regular'
                                        ? theme === 'dark'
                                          ? 'bg-green-900/50 text-green-300 border border-green-700'
                                          : 'bg-green-100 text-green-700'
                                        : theme === 'dark'
                                          ? 'bg-gray-700 text-gray-300 border border-gray-600'
                                          : 'bg-gray-100 text-gray-600'
                              }`}>
                                {customer.customer_type === 'vip' ? 'VIP' :
                                 customer.customer_type === 'business' ? 'Business' :
                                 customer.customer_type === 'wholesale' ? 'Wholesale' :
                                 customer.customer_type === 'regular' ? 'Regular' :
                                 'Retail'}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {customer.phone}
                          </p>
                        </div>
                        {selectedCustomer?.id === customer.id && (
                          <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                  ) : (
                    <div className="flex flex-col justify-center items-center py-8">
                      <Users size={32} className={`mb-2 opacity-50 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <span className={`text-sm mb-2 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        No customers found
                      </span>
                      <button
                        onClick={() => loadCustomers()}
                        className="text-blue-500 hover:text-blue-600 text-xs"
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Cart & Checkout */}
        <div className={`${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        } rounded-xl sm:rounded-2xl border ${
          theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
        }`}>
          <div className="p-4 sm:p-6">
            {/* Cart Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className={`text-lg sm:text-xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Cart ({cart.length})
              </h3>
              {cart.length > 0 && (
                <button
                  onClick={clearCart}
                  className="text-red-500 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 touch-manipulation"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            {/* Selected Customer */}
            <div className={`p-3 rounded-lg sm:rounded-xl mb-4 ${
              theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Customer
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm truncate ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {selectedCustomer 
                        ? selectedCustomer.name 
                        : isWalkInMode 
                          ? (walkInCustomer.name.trim() || 'Walk-in Customer')
                          : 'No customer selected'}
                    </p>
                    {selectedCustomer?.customer_type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                        selectedCustomer.customer_type === 'vip'
                          ? theme === 'dark'
                            ? 'bg-purple-900/50 text-purple-300 border border-purple-700'
                            : 'bg-purple-100 text-purple-700'
                          : selectedCustomer.customer_type === 'business'
                            ? theme === 'dark'
                              ? 'bg-blue-900/50 text-blue-300 border border-blue-700'
                              : 'bg-blue-100 text-blue-700'
                            : selectedCustomer.customer_type === 'wholesale'
                              ? theme === 'dark'
                                ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-700'
                                : 'bg-indigo-100 text-indigo-700'
                              : selectedCustomer.customer_type === 'regular'
                                ? theme === 'dark'
                                  ? 'bg-green-900/50 text-green-300 border border-green-700'
                                  : 'bg-green-100 text-green-700'
                                : theme === 'dark'
                                  ? 'bg-gray-700 text-gray-300 border border-gray-600'
                                  : 'bg-gray-100 text-gray-600'
                      }`}>
                        {selectedCustomer.customer_type === 'vip' ? 'VIP' :
                         selectedCustomer.customer_type === 'business' ? 'Business' :
                         selectedCustomer.customer_type === 'wholesale' ? 'Wholesale' :
                         selectedCustomer.customer_type === 'regular' ? 'Regular' :
                         'Retail'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setActiveTab('customers')}
                  className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-blue-400 hover:bg-slate-600'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white'
                  }`}
                >
                  <User size={16} />
                </button>
              </div>
            </div>

            {/* Cart Items */}
            <div className="max-h-40 sm:max-h-48 overflow-y-auto mb-4">
              {cart.length === 0 ? (
                <div className="text-center py-6 sm:py-8">
                  <ShoppingCart className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 ${
                    theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                  }`} />
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Cart is empty
                  </p>
                </div>
              ) : (
                <div className="space-y-1 sm:space-y-2">
                  {cart.map(item => (
                    <div
                      key={item.product.id}
                      className={`p-2 sm:p-2.5 rounded border ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-medium text-xs sm:text-sm truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {item.product.name}
                          </h4>
                          <p className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {formatCurrency(item.product.selling_price)} × {item.quantity} = <span className={`font-bold ${
                              theme === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`}>{formatCurrency(Number(item.product.selling_price) * item.quantity)}</span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <button
                            onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                            className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg border flex items-center justify-center touch-manipulation ${
                              theme === 'dark'
                                ? 'border-slate-600 text-gray-400 hover:text-white hover:bg-slate-600'
                                : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            <Minus size={10} />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleQuantityInputChange(item.product.id, e.target.value)}
                            className={`h-6 sm:h-7 text-xs font-medium border rounded-lg touch-manipulation ${
                              theme === 'dark'
                                ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                            } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                            style={{ 
                              width: '35px',
                              textAlign: 'center',
                              padding: '0',
                              margin: '0',
                              boxSizing: 'border-box',
                              MozAppearance: 'textfield',
                              WebkitAppearance: 'none'
                            } as React.CSSProperties & {
                              MozAppearance?: string;
                              WebkitAppearance?: string;
                            }}
                          />
                          <button
                            onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                            className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg border flex items-center justify-center touch-manipulation ${
                              theme === 'dark'
                                ? 'border-slate-600 text-gray-400 hover:text-white hover:bg-slate-600'
                                : 'border-gray-300 text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            <Plus size={10} />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.product.id)}
                            className="text-red-500 hover:text-red-600 p-0.5"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Checkout Section */}
            {cart.length > 0 && (
              <div className="space-y-4">
                {/* Payment Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Discount (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={discount === 0 ? '' : discount}
                      onChange={(e) => setDiscount(e.target.value === '' ? 0 : Number(e.target.value))}
                      placeholder="0"
                      className={`w-full px-3 py-2 text-sm rounded-lg border ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Payment ({currencySymbol})
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={paidAmount === 0 ? '' : paidAmount}
                      onChange={(e) => setPaidAmount(e.target.value === '' ? 0 : Number(e.target.value))}
                      placeholder="0.00"
                      className={`w-full px-3 py-2 text-sm rounded-lg border ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  </div>
                </div>

                {/* Tax Option */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="includeTax"
                      checked={includeTax}
                      onChange={(e) => setIncludeTax(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="includeTax" className={`text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Include Tax
                    </label>
                  </div>
                  {includeTax && (
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value))}
                      placeholder="Tax %"
                      className={`w-20 px-2 py-1 text-sm rounded border ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                    />
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Payment Method
                  </label>
                  <div className="flex gap-3">
                    {['cash', 'bank_transfer', 'other'].map(method => (
                      <button
                        key={method}
                        onClick={() => setPaymentMethod(method as any)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors min-w-[85px] whitespace-nowrap ${
                          paymentMethod === method
                            ? 'bg-blue-600 text-white'
                            : theme === 'dark'
                              ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {method === 'bank_transfer' ? 'Bank Transfer' : method === 'other' ? 'Other' : 'Cash'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className={`p-4 rounded-lg space-y-2 text-sm ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'
                }`}>
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(safeSubtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({discount}%):</span>
                      <span>-{formatCurrency(safeDiscountAmount)}</span>
                    </div>
                  )}
                  {includeTax && safeTaxAmount > 0 && (
                    <div className="flex justify-between">
                      <span>Tax ({taxRate}%):</span>
                      <span>{formatCurrency(safeTaxAmount)}</span>
                    </div>
                  )}
                  <div className={`flex justify-between font-bold text-lg pt-2 border-t ${
                    theme === 'dark' ? 'border-slate-600' : 'border-gray-200'
                  }`}>
                    <span>Total:</span>
                    <span>{formatCurrency(safeTotal)}</span>
                  </div>
                  {paidAmount > 0 && paidAmount !== total && (
                    <div className={`flex justify-between font-medium ${
                      paidAmount < total ? 'text-red-600' : 'text-green-600'
                    }`}>
                      <span>{paidAmount < total ? 'Due Amount:' : 'Change:'}</span>
                      <span>{formatCurrency(Math.abs(total - paidAmount))}</span>
                    </div>
                  )}
                </div>

                {/* Checkout Button */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={printPreview}
                    disabled={cart.length === 0}
                    className={`py-3 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
                      cart.length === 0
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : theme === 'dark'
                          ? 'bg-slate-600 text-white hover:bg-slate-700'
                          : 'bg-gray-600 text-white hover:bg-gray-700'
                    }`}
                  >
                    <Printer size={16} />
                    <span>Preview</span>
                  </button>
                  <button
                    onClick={processSale}
                    disabled={isProcessing || paidAmount <= 0}
                    className={`py-3 rounded-xl font-medium transition-all ${
                      isProcessing || paidAmount <= 0
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700 active:scale-95'
                    }`}
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <CreditCard size={16} />
                        <span>Complete Sale</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Print Receipt Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrintModal}
        onConfirm={handlePrintConfirm}
        onClose={handlePrintCancel}
        title="Print Receipt"
        message="Would you like to print the receipt for this sale?"
        confirmText="Print"
        cancelText="Skip"
        type="info"
        isLoading={printModalLoading}
      />
      
      {/* Product Preview Modal */}
      <ProductPreviewModal
        isOpen={!!previewProduct}
        product={previewProduct || {} as Product}
        onClose={() => setPreviewProduct(null)}
        isLoading={previewProductLoading}
      />
    </div>
  );
};

export default POSPage;